import { STICKER_HEADERS, type ImportedStickerRow } from './domain'
import { normalizeText } from './normalization'

export type SpreadsheetCell = string | number | boolean | Date | null | undefined
export type SpreadsheetMatrix = { name: string; rows: SpreadsheetCell[][] }
export type SpreadsheetImportFormat = 'canonical' | 'social-actions' | 'call-center'

export type ParsedSpreadsheetImport = {
  format: SpreadsheetImportFormat
  sheetName: string
  rows: ImportedStickerRow[]
  reviewCount: number
}

type HeaderMatch = {
  format: SpreadsheetImportFormat
  score: number
  sheetName: string
  headerIndex: number
  headers: string[]
  rows: SpreadsheetCell[][]
}

const HEADER_ALIASES: Record<string, string[]> = {
  name: ['NOME', 'NOME COMPLETO', 'NOME DO DESTINATARIO', 'NOME DO DESTINATÁRIO', 'DESTINATARIO', 'DESTINATÁRIO', 'CLIENTE'],
  phone: ['TELEFONE', 'WHATSAPP', 'CELULAR'],
  address: ['ENDERECO', 'ENDEREÇO', 'LOGRADOURO'],
  number: ['NUMERO', 'NÚMERO'],
  complement: ['COMPLEMENTO'],
  cep: ['CEP'],
  neighborhood: ['BAIRRO', 'BAIRRO REGIAO', 'BAIRRO REGIÃO'],
  city: ['CIDADE', 'MUNICIPIO', 'MUNICÍPIO'],
  state: ['ESTADO', 'UF'],
  date: ['DATA', 'RECEBIDO EM'],
  material: ['TIPO', 'MATERIAL', 'MODELO ESCOLHIDO'],
  origin: ['ORIGEM'],
  status: ['STATUS'],
}

const CITY_ALIASES: Array<[RegExp, string]> = [
  [/\bBELFORD ROXO\b|\bB ROXO\b/, 'Belford Roxo'],
  [/\bDUQUE DE CAXIAS\b|\bCAXIAS\b/, 'Duque de Caxias'],
  [/\bNOVA IGUACU\b/, 'Nova Iguaçu'],
  [/\bSAO JOAO DE MERITI\b|\bS J MERITI\b/, 'São João de Meriti'],
  [/\bSAO GONCALO\b/, 'São Gonçalo'],
  [/\bNITEROI\b/, 'Niterói'],
  [/\bMESQUITA\b/, 'Mesquita'],
  [/\bNILOPOLIS\b/, 'Nilópolis'],
  [/\bQUEIMADOS\b/, 'Queimados'],
  [/\bITAGUAI\b/, 'Itaguaí'],
  [/\bMAGE\b/, 'Magé'],
  [/\bMARICA\b/, 'Maricá'],
  [/\bITABORAI\b/, 'Itaboraí'],
  [/\bPETROPOLIS\b/, 'Petrópolis'],
  [/\bMACAE\b/, 'Macaé'],
]

function cellText(value: SpreadsheetCell): string {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : String(value)
  return String(value).trim()
}

function normalizedHeaders(row: SpreadsheetCell[]): string[] {
  return row.map((value) => normalizeText(cellText(value)))
}

function includes(headers: string[], aliases: string[]): boolean {
  const normalized = aliases.map(normalizeText)
  return headers.some((header) => normalized.includes(header))
}

function detectHeader(sheets: SpreadsheetMatrix[]): HeaderMatch {
  const candidates: HeaderMatch[] = []
  for (const sheet of sheets) {
    sheet.rows.slice(0, 30).forEach((row, headerIndex) => {
      const headers = normalizedHeaders(row)
      const hasName = includes(headers, HEADER_ALIASES.name)
      const hasPhone = includes(headers, HEADER_ALIASES.phone)
      const hasAddress = includes(headers, HEADER_ALIASES.address)
      if (!hasName || !hasPhone || !hasAddress) return

      const canonicalRequired = ['CEP', 'LOGRADOURO', 'NUMERO', 'BAIRRO', 'CIDADE', 'ESTADO']
      const canonicalMatches = canonicalRequired.filter((header) => headers.includes(header)).length
      if (canonicalMatches === canonicalRequired.length) {
        candidates.push({ format: 'canonical', score: 300, sheetName: sheet.name, headerIndex, headers, rows: sheet.rows })
        return
      }
      if (includes(headers, HEADER_ALIASES.material) && includes(headers, HEADER_ALIASES.origin)) {
        candidates.push({ format: 'social-actions', score: 200, sheetName: sheet.name, headerIndex, headers, rows: sheet.rows })
        return
      }
      if (includes(headers, HEADER_ALIASES.date) || includes(headers, HEADER_ALIASES.cep) || includes(headers, HEADER_ALIASES.complement)) {
        candidates.push({ format: 'call-center', score: 100, sheetName: sheet.name, headerIndex, headers, rows: sheet.rows })
      }
    })
  }
  const selected = candidates.sort((a, b) => b.score - a.score || a.headerIndex - b.headerIndex)[0]
  if (!selected) throw new Error('Nenhuma aba possui um cabeçalho reconhecido com nome, telefone e endereço')
  return selected
}

function column(headers: string[], aliases: string[]): number {
  const normalized = aliases.map(normalizeText)
  return headers.findIndex((header) => normalized.includes(header))
}

function valueAt(row: SpreadsheetCell[], index: number): string {
  return index >= 0 ? cellText(row[index]) : ''
}

function dateFromExcelSerial(value: number): Date {
  return new Date(Date.UTC(1899, 11, 30) + Math.round(value * 86_400_000))
}

function calendarDate(value: SpreadsheetCell): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return `${value.toISOString().slice(0, 10)} 12:00:00`
  if (typeof value === 'number' && value > 20_000 && value < 100_000) {
    return `${dateFromExcelSerial(value).toISOString().slice(0, 10)} 12:00:00`
  }
  const text = cellText(value)
  const brazilian = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4,5})/)
  if (brazilian) {
    let year = brazilian[3]
    // Algumas planilhas do call center trazem 23026 para 2026. Para anos com
    // cinco dígitos, o século e os dois últimos dígitos são a parte confiável.
    if (year.length === 5 && year.startsWith('2')) year = `20${year.slice(-2)}`
    const day = brazilian[1].padStart(2, '0')
    const month = brazilian[2].padStart(2, '0')
    const iso = `${year}-${month}-${day}`
    const parsed = new Date(`${iso}T12:00:00Z`)
    if (!Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === iso) return `${iso} 12:00:00`
  }
  const iso = text.match(/^(\d{4}-\d{2}-\d{2})/)
  return iso ? `${iso[1]} 12:00:00` : ''
}

function parseModels(value: string): string[] {
  return value.split(/[+|,;]/).map((item) => item.trim()).filter(Boolean)
}

function inferCity(address: string): string {
  const normalized = normalizeText(address)
  return CITY_ALIASES.find(([pattern]) => pattern.test(normalized))?.[1] ?? 'A classificar'
}

function inferExplicitNeighborhood(address: string, complement: string): string {
  const text = `${address} ${complement}`.replace(/\s+/g, ' ').trim()
  const match = text.match(/\bBAIRRO\s*[:.-]?\s*([\p{L}][\p{L}\s']{1,60}?)(?=\s*(?:[-,;·]|\bCEP\b|\b(?:RIO DE JANEIRO|RJ|MESQUITA|PETROPOLIS|PETRÓPOLIS|QUEIMADOS|CASIMIRO DE ABREU)\b|$))/iu)
  return match?.[1]?.trim() ?? ''
}

function addressParts(addressInput: string, explicitCep = '', explicitComplement = '') {
  const original = addressInput.replace(/\s+/g, ' ').trim()
  const cepMatch = `${explicitCep} ${original}`.match(/\b(\d{5})[- ]?(\d{3})\b/)
  const cep = cepMatch ? `${cepMatch[1]}${cepMatch[2]}` : ''
  const withoutCep = original.replace(/\bCEP\s*[:.-]?\s*\d{5}[- ]?\d{3}\b/ig, '').replace(/\s+,/g, ',').trim()
  const candidates = [...withoutCep.matchAll(/\b(?:N[º°.]?\s*)?(?:LT\s*|LOTE\s*)?(\d+[A-Z]?)\b/gi)]
  const numberMatch = candidates.find((match) => {
    const prefix = normalizeText(withoutCep.slice(Math.max(0, (match.index ?? 0) - 18), match.index ?? 0))
    return !/(?:RUA|AVENIDA|AV|ESTRADA|TRAVESSA|RODOVIA|AP|APT|APARTAMENTO|BLOCO|CASA|SALA|ANDAR)$/.test(prefix)
  })
  const number = numberMatch?.[0]?.replace(/^N[º°.]?\s*/i, '').trim() || 'S/N'
  const index = numberMatch?.index ?? -1
  const street = (index >= 0 ? withoutCep.slice(0, index) : withoutCep).replace(/[\s,.-]+$/g, '').trim() || withoutCep || 'Endereço não informado'
  const trailing = index >= 0 ? withoutCep.slice(index + (numberMatch?.[0].length ?? 0)).replace(/^[\s,.-]+/g, '').trim() : ''
  const complement = [explicitComplement.trim(), trailing].filter(Boolean).join(' · ')
  const neighborhood = inferExplicitNeighborhood(original, explicitComplement)
  const city = inferCity(original)
  return {
    cep,
    street,
    streetNumber: number,
    complement: complement || undefined,
    neighborhood: neighborhood || 'A classificar',
    city,
    state: city === 'A classificar' ? '' : 'RJ',
  }
}

function canonicalRows(match: HeaderMatch): ImportedStickerRow[] {
  const headers = match.headers
  const data = match.rows.slice(match.headerIndex + 1)
  const indexes = Object.fromEntries(Object.entries(HEADER_ALIASES).map(([key, aliases]) => [key, column(headers, aliases)])) as Record<string, number>
  const canonicalIndex = (label: string) => headers.findIndex((header) => header === normalizeText(label))
  return data.flatMap((row, offset) => {
    const recipientName = valueAt(row, indexes.name)
    const phone = valueAt(row, indexes.phone)
    const street = valueAt(row, canonicalIndex(STICKER_HEADERS[7]))
    if (!recipientName && !phone && !street) return []
    return [{
      sourceRowNumber: match.headerIndex + offset + 2,
      receivedAt: calendarDate(row[canonicalIndex(STICKER_HEADERS[0])]),
      source: valueAt(row, canonicalIndex(STICKER_HEADERS[2])),
      page: valueAt(row, canonicalIndex(STICKER_HEADERS[3])),
      recipientName,
      phone,
      cep: valueAt(row, canonicalIndex(STICKER_HEADERS[6])),
      street,
      streetNumber: valueAt(row, canonicalIndex(STICKER_HEADERS[8])),
      complement: valueAt(row, canonicalIndex(STICKER_HEADERS[9])) || undefined,
      neighborhood: valueAt(row, canonicalIndex(STICKER_HEADERS[10])),
      city: valueAt(row, canonicalIndex(STICKER_HEADERS[11])),
      state: valueAt(row, canonicalIndex(STICKER_HEADERS[12])),
      referencePoint: valueAt(row, canonicalIndex(STICKER_HEADERS[13])) || undefined,
      quantity: Math.max(1, Number.parseInt(valueAt(row, canonicalIndex(STICKER_HEADERS[14])).replace(/\D/g, ''), 10) || 1),
      consent: ['SIM', 'TRUE', '1', 'ACEITO', 'ACEITA'].includes(normalizeText(valueAt(row, canonicalIndex(STICKER_HEADERS[15])))),
      status: valueAt(row, canonicalIndex(STICKER_HEADERS[16])) || undefined,
      utmSource: valueAt(row, canonicalIndex(STICKER_HEADERS[17])) || undefined,
      utmMedium: valueAt(row, canonicalIndex(STICKER_HEADERS[18])) || undefined,
      utmCampaign: valueAt(row, canonicalIndex(STICKER_HEADERS[19])) || undefined,
      externalId: valueAt(row, canonicalIndex(STICKER_HEADERS[21])) || undefined,
      stickerModels: parseModels(valueAt(row, canonicalIndex(STICKER_HEADERS[22]))),
    }]
  })
}

function legacyRows(match: HeaderMatch): ImportedStickerRow[] {
  const indexes = Object.fromEntries(Object.entries(HEADER_ALIASES).map(([key, aliases]) => [key, column(match.headers, aliases)])) as Record<string, number>
  return match.rows.slice(match.headerIndex + 1).flatMap((row, offset) => {
    const recipientName = valueAt(row, indexes.name)
    const phone = valueAt(row, indexes.phone)
    const address = valueAt(row, indexes.address)
    if (!recipientName && !phone && !address) return []
    const parsedAddress = addressParts(address, valueAt(row, indexes.cep), valueAt(row, indexes.complement))
    const explicitNeighborhood = valueAt(row, indexes.neighborhood)
    const explicitCity = valueAt(row, indexes.city)
    const explicitState = valueAt(row, indexes.state)
    return [{
      sourceRowNumber: match.headerIndex + offset + 2,
      receivedAt: calendarDate(row[indexes.date]),
      source: valueAt(row, indexes.origin) || (match.format === 'social-actions' ? 'planilha_redes_sociais' : 'planilha_call_center'),
      recipientName,
      phone,
      ...parsedAddress,
      neighborhood: explicitNeighborhood || parsedAddress.neighborhood,
      city: explicitCity || parsedAddress.city,
      state: explicitState || parsedAddress.state,
      quantity: 1,
      consent: false,
      status: valueAt(row, indexes.status) || undefined,
      stickerModels: parseModels(valueAt(row, indexes.material)),
      needsAddressReview: true,
    }]
  })
}

export function parseSpreadsheetMatrices(sheets: SpreadsheetMatrix[]): ParsedSpreadsheetImport {
  const match = detectHeader(sheets)
  const rows = match.format === 'canonical' ? canonicalRows(match) : legacyRows(match)
  if (!rows.length) throw new Error('A aba reconhecida não contém pedidos preenchidos')
  return {
    format: match.format,
    sheetName: match.sheetName,
    rows,
    reviewCount: rows.filter((row) => row.needsAddressReview).length,
  }
}
